import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Activity, Settings, Send, CheckCircle, XCircle, AlertCircle, Eye, EyeOff, ExternalLink, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MetaCAPI() {
  const [settings, setSettings] = useState({
    capi_enabled: false,
    pixel_id: '',
    capi_access_token: '',
    capi_test_mode: false,
    capi_test_code: ''
  });
  const [events, setEvents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [hasExistingToken, setHasExistingToken] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchEvents();
  }, []);

  const fetchSettings = async () => {
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/clinic/${clinicId}/capi-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({
          ...prev,
          capi_enabled: data.capi_enabled || false,
          pixel_id: data.pixel_id || '',
          capi_test_mode: data.capi_test_mode || false
        }));
        setHasExistingToken(data.has_access_token || false);
      }
    } catch (error) {
      console.error('Error fetching CAPI settings:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/clinic/${clinicId}/capi-events?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEvents(data.events || []);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      
      const payload = {
        capi_enabled: settings.capi_enabled,
        pixel_id: settings.pixel_id,
        capi_test_mode: settings.capi_test_mode,
        capi_test_code: settings.capi_test_code
      };
      
      // Only include token if user entered a new one
      if (settings.capi_access_token) {
        payload.capi_access_token = settings.capi_access_token;
      }
      
      const response = await fetch(`${API}/api/clinic/${clinicId}/capi-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        alert('✅ Configuración guardada');
        fetchSettings();
      } else {
        alert('❌ Error al guardar');
      }
    } catch (error) {
      alert('❌ Error: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const sendTestEvent = async (eventName) => {
    setTesting(true);
    setTestResult(null);
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/clinic/${clinicId}/capi-test?event_name=${eventName}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTestResult(data);
      if (data.success) {
        fetchEvents();
      }
    } catch (error) {
      setTestResult({ success: false, error: error.message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="meta-capi-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Activity className="w-7 h-7 text-blue-500" />
              Meta Conversions API
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Envía eventos server-side a Meta para mejor atribución de conversiones
            </p>
          </div>
          <a 
            href="https://developers.facebook.com/docs/marketing-api/conversions-api" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-600 flex items-center gap-1 text-sm"
          >
            Documentación <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        {/* Configuration Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configuración
          </h2>
          
          <div className="space-y-4">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Activar CAPI</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Envía eventos Lead, Schedule y Purchase a Meta
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.capi_enabled}
                  onChange={(e) => setSettings({...settings, capi_enabled: e.target.checked})}
                  className="sr-only peer"
                  data-testid="capi-enabled-toggle"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Pixel ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Pixel ID
              </label>
              <input
                type="text"
                value={settings.pixel_id}
                onChange={(e) => setSettings({...settings, pixel_id: e.target.value})}
                placeholder="Ej: 1234567890123456"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                data-testid="pixel-id-input"
                autoComplete="off"
              />
              <p className="text-xs text-gray-500 mt-1">
                Encuéntralo en Events Manager → Data Sources → Tu Pixel
              </p>
            </div>

            {/* Access Token */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Access Token de CAPI
              </label>
              <div className="relative">
                <input
                  type={showToken ? "text" : "password"}
                  value={settings.capi_access_token}
                  onChange={(e) => setSettings({...settings, capi_access_token: e.target.value})}
                  placeholder={hasExistingToken ? "••••••••••••••••• (token guardado)" : "Pega tu access token aquí"}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  data-testid="capi-token-input"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Genera en Events Manager → Settings → Conversions API → Generate Access Token
              </p>
              {hasExistingToken && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Token configurado
                </p>
              )}
            </div>

            {/* Test Mode */}
            <div className="flex items-center justify-between p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Modo de Prueba</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Los eventos aparecerán en Test Events en Events Manager
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.capi_test_mode}
                  onChange={(e) => setSettings({...settings, capi_test_mode: e.target.checked})}
                  className="sr-only peer"
                  data-testid="test-mode-toggle"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:ring-2 peer-focus:ring-yellow-300 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
              </label>
            </div>

            {settings.capi_test_mode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Test Event Code (opcional)
                </label>
                <input
                  type="text"
                  value={settings.capi_test_code}
                  onChange={(e) => setSettings({...settings, capi_test_code: e.target.value})}
                  placeholder="Ej: TEST12345"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            {/* Save Button */}
            <button
              onClick={saveSettings}
              disabled={saving}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="save-capi-settings-btn"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                'Guardar Configuración'
              )}
            </button>
          </div>
        </div>

        {/* Test Events Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Send className="w-5 h-5" />
            Enviar Evento de Prueba
          </h2>
          
          <div className="grid grid-cols-3 gap-4 mb-4">
            <button
              onClick={() => sendTestEvent('Lead')}
              disabled={testing || !settings.capi_enabled}
              className="py-3 px-4 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition disabled:opacity-50"
              data-testid="test-lead-event-btn"
            >
              Lead
            </button>
            <button
              onClick={() => sendTestEvent('Schedule')}
              disabled={testing || !settings.capi_enabled}
              className="py-3 px-4 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition disabled:opacity-50"
              data-testid="test-schedule-event-btn"
            >
              Schedule
            </button>
            <button
              onClick={() => sendTestEvent('Purchase')}
              disabled={testing || !settings.capi_enabled}
              className="py-3 px-4 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 font-medium rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition disabled:opacity-50"
              data-testid="test-purchase-event-btn"
            >
              Purchase
            </button>
          </div>

          {testing && (
            <div className="flex items-center justify-center py-4 text-gray-500">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" />
              Enviando evento...
            </div>
          )}

          {testResult && (
            <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
              <div className="flex items-center gap-2 mb-2">
                {testResult.success ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="font-medium text-green-700 dark:text-green-400">Evento enviado correctamente</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5 text-red-500" />
                    <span className="font-medium text-red-700 dark:text-red-400">Error al enviar evento</span>
                  </>
                )}
              </div>
              <pre className="text-xs bg-gray-100 dark:bg-gray-900 p-2 rounded overflow-x-auto">
                {JSON.stringify(testResult, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Events History */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Historial de Eventos
            </h2>
            <button
              onClick={fetchEvents}
              className="text-blue-500 hover:text-blue-600 flex items-center gap-1 text-sm"
            >
              <RefreshCw className="w-4 h-4" /> Actualizar
            </button>
          </div>

          {events.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No hay eventos registrados aún</p>
              <p className="text-sm">Los eventos aparecerán aquí cuando se envíen</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {events.map((event, idx) => (
                <div
                  key={idx}
                  className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      event.event_name === 'Lead' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                      event.event_name === 'Schedule' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                      'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                    }`}>
                      {event.event_name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(event.created_at).toLocaleString('es-MX')}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    <span className="font-medium">ID:</span> {event.event_id?.substring(0, 30)}...
                  </div>
                  {event.response?.events_received && (
                    <div className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      {event.response.events_received} evento(s) recibido(s) por Meta
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
            <h3 className="font-semibold text-green-800 dark:text-green-400 mb-2">📥 Lead</h3>
            <p className="text-sm text-green-700 dark:text-green-300">
              Se envía automáticamente cuando entra un nuevo lead desde Meta Ads, WhatsApp o landing page.
            </p>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
            <h3 className="font-semibold text-blue-800 dark:text-blue-400 mb-2">📅 Schedule</h3>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Se envía cuando un paciente agenda una cita, ya sea manual o vía WhatsApp.
            </p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 border border-purple-200 dark:border-purple-800">
            <h3 className="font-semibold text-purple-800 dark:text-purple-400 mb-2">💰 Purchase</h3>
            <p className="text-sm text-purple-700 dark:text-purple-300">
              Envíalo manualmente cuando el paciente se opera. Incluye valor de la cirugía para ROAS.
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
